import { AccountState, TransitionTrigger, type StatusBucket } from './account-state.enum';
import type { StructuredStatus } from '../interfaces/warmup.interface';
import { AccountStatus } from './email.enum';
import { SENDING_STATUSES, WarmupStatus } from './warmup.enum';

/**
 * Runtime lookup tables + helper functions for the `AccountState` machine.
 *
 * Split out of `account-state.enum.ts` per the package charter (knowledge-base
 * 11-conventions.md / packages/types): a `*.enum.ts` file holds ONLY enum/type
 * declarations, no runtime values or functions. These are re-exported from
 * `enums/index.ts` alongside the enum, so `@trulyinbox/types` consumers and the
 * `../enums` barrel keep resolving every symbol unchanged.
 */

/**
 * `warmupPausedReason` written when a mailbox is suspended into
 * `SUSPENDED_PROVIDER` because its PARTNER's callback has failed
 * `PARTNER_OUTAGE_SUSPEND_THRESHOLD` refreshes in a row.
 *
 * **Shared because two apps must agree on it**, which is what separates it from
 * its siblings (`AUTH_FAILED`, `SEND_FAILURES_EXCEEDED`) that each live in the
 * one app that uses them: warmup-worker WRITES this value and email-worker's
 * recovery sweep SELECTS on it. It is an internal wire value in an
 * unconstrained `varchar` — nothing would fail the build on a typo, the sweep
 * would simply match no rows forever, and every suspended mailbox would sit
 * there until a human resumed it by hand. Same rule as the BullMQ job names in
 * `packages/queue` (11-conventions §17: never inline an internal cross-app wire
 * value).
 */
export const SUSPEND_REASON_PARTNER_UNAVAILABLE = 'PARTNER_UNAVAILABLE';

/**
 * The 7 public `?status=` buckets (excludes the internal `setup` bucket,
 * which still covers the other five connection states). Phase 7 builds its
 * filter enum from this list.
 */
export const PUBLIC_STATUS_BUCKETS: readonly StatusBucket[] = [
  'active',
  'throttled',
  'paused',
  'suspended',
  'cooldown',
  'maintenance',
  'disconnected',
];

/**
 * Total map from every `AccountState` to its `StatusBucket` (plan §3.2).
 * Typed as `Record<AccountState, StatusBucket>` so a missing state is a
 * compile error — this is what guarantees the "covers all states" invariant.
 *
 * STATE-ONLY, so it never yields `cooldown`: that bucket depends on the
 * suspend reason too. Anything rendering or filtering a bucket goes through
 * {@link statusBucketFor}, which refines this map.
 */
export const ACCOUNT_STATE_BUCKET: Record<AccountState, StatusBucket> = {
  [AccountState.WARMUP_ACTIVE]: 'active',
  [AccountState.WARMUP_THROTTLED]: 'throttled',
  [AccountState.WARMUP_PAUSED]: 'paused',
  [AccountState.SUSPENDED_AUTH]: 'suspended',
  [AccountState.SUSPENDED_BOUNCE]: 'suspended',
  [AccountState.SUSPENDED_SPAM]: 'suspended',
  [AccountState.SUSPENDED_BLACKLIST]: 'suspended',
  [AccountState.SUSPENDED_PROVIDER]: 'suspended',
  [AccountState.SUSPENDED_PLATFORM]: 'suspended',
  [AccountState.SUSPENDED_VERIFICATION]: 'suspended',
  [AccountState.MAINTENANCE]: 'maintenance',
  [AccountState.DISCONNECTED]: 'disconnected',
  [AccountState.CONNECTED]: 'setup',
  [AccountState.DNS_CHECKING]: 'setup',
  [AccountState.DNS_FAILED]: 'setup',
  [AccountState.DNS_PASSED]: 'setup',
  // Mailbox verification is part of getting a mailbox ready to warm, so both
  // states live in the internal `setup` bucket alongside the DNS ones — not in
  // `paused`/`suspended`, which would misreport a mailbox that has never warmed
  // as one that stopped.
  [AccountState.MAILBOX_VERIFYING]: 'setup',
  [AccountState.MAILBOX_VERIFY_FAILED]: 'setup',
  [AccountState.WARMUP_READY]: 'setup',
};

/**
 * The `AccountState` values in which an account is actively sending warmup mail
 * — the canonical counterpart of the legacy `SENDING_STATUSES`
 * (`warmup.enum.ts`), which says the same thing about the mirror column.
 *
 * Deliberately its OWN constant rather than a reuse of
 * {@link MANUALLY_PAUSABLE_STATES}, whose membership is identical today. They
 * answer different questions — "is this mailbox sending" versus "may the user
 * press Pause on it" — and the moment a state joins one but not the other, a
 * shared constant silently gives the wrong answer to whichever caller it was
 * not written for. Same reasoning as `ACTIVELY_WARMING_STATUSES` being kept
 * separate from `SENDING_STATUSES` despite matching membership.
 */
export const SENDING_STATES: ReadonlyArray<AccountState> = [
  AccountState.WARMUP_ACTIVE,
  AccountState.WARMUP_THROTTLED,
  AccountState.MAINTENANCE,
];

/**
 * The states in which an account has NEVER warmed — it is still being set up.
 *
 * Used by the mailbox-verification gate to decide what a verdict means for the
 * account: a pre-warmup mailbox always tests BOTH directions, waits for both
 * before warmup starts, and lands in `MAILBOX_VERIFY_FAILED` on a failure rather
 * than being suspended. It no longer buys a longer retry ladder — every account
 * gets the same three probe sends.
 *
 * ⚠ NOT the complement of {@link SENDING_STATES}. `WARMUP_PAUSED`, every
 * `SUSPENDED_*` and `DISCONNECTED` are in neither set: those accounts are not
 * sending, but they are not new either — they have a history, which is the
 * distinction this constant exists to draw. Deriving one from the other would
 * quietly treat a suspended account as a new mailbox, testing directions it was
 * never flagged for.
 */
export const PRE_WARMUP_STATES: ReadonlyArray<AccountState> = [
  AccountState.CONNECTED,
  AccountState.DNS_CHECKING,
  AccountState.DNS_PASSED,
  AccountState.MAILBOX_VERIFYING,
  AccountState.WARMUP_READY,
];

/** The seven system-initiated suspended states. */
export const SUSPENDED_STATES: ReadonlyArray<AccountState> = [
  AccountState.SUSPENDED_AUTH,
  AccountState.SUSPENDED_BOUNCE,
  AccountState.SUSPENDED_SPAM,
  AccountState.SUSPENDED_BLACKLIST,
  AccountState.SUSPENDED_PROVIDER,
  AccountState.SUSPENDED_PLATFORM,
  AccountState.SUSPENDED_VERIFICATION,
];

// Shared transition target sets used to keep the table DRY and consistent.
const ANY_WARMUP_TO_SUSPENDED: AccountState[] = [...SUSPENDED_STATES];
// Recovery from a suspended state restores the snapshotted prior warmup state.
const SUSPENDED_RECOVERY_TARGETS: AccountState[] = [
  AccountState.WARMUP_ACTIVE,
  AccountState.WARMUP_THROTTLED,
  AccountState.MAINTENANCE,
  AccountState.WARMUP_PAUSED,
  AccountState.DISCONNECTED,
];

/**
 * The guard's data: a TOTAL map of every `AccountState` to its legal next
 * states (plan §3.3). Every state has an entry even if `[]`. Rules baked in:
 * any warmup state → `SUSPENDED_*` (critical event); `SUSPENDED_*` →
 * snapshotted prior warmup state; any state → `DISCONNECTED` (user disconnect).
 * Hard-blocked transitions are ABSENT: `DNS_FAILED→WARMUP_ACTIVE`,
 * `DISCONNECTED→WARMUP_ACTIVE`, `MAILBOX_VERIFY_FAILED→WARMUP_ACTIVE`.
 *
 * ⚠ This table is defence in depth, NOT the mailbox-verification gate. The
 * `CONNECTED → WARMUP_ACTIVE` edge below is retained deliberately (removing it
 * breaks `enableAccount` for reasons unrelated to verification), so an
 * unverified CONNECTED account is not stopped here. The gate is enforced by
 * the three callers that check the persisted verification row immediately
 * before calling `transition`: `enableAccount`, `promoteFromDnsPass` and the
 * verification orchestrator itself.
 */
export const ALLOWED_TRANSITIONS: Record<AccountState, AccountState[]> = {
  [AccountState.DISCONNECTED]: [AccountState.CONNECTED],
  // CONNECTED -> WARMUP_ACTIVE is the user-enable edge. No backend writer moves
  // an account into WARMUP_READY/DNS_PASSED today (the column defaults to
  // CONNECTED), so a fresh account is CONNECTED at enable time. enableWarmup
  // enforces the DNS hard-gate + plan limits in apps/api BEFORE enqueuing the
  // ENABLED event, so by the time the worker transitions, the account is
  // semantically ready. Without this edge the guard would reject the enable and
  // accountState would never reach WARMUP_ACTIVE (the X1 enable-drift bug).
  [AccountState.CONNECTED]: [
    AccountState.DNS_CHECKING,
    // A freshly-connected account is CONNECTED when its first mailbox check
    // starts (nothing moves it into DNS_PASSED — see the comment above), so
    // without this edge `beginMailboxVerification` is refused for exactly the
    // population the verification states were built for: the badge never reads
    // "Checking mailbox", a failure never lands in MAILBOX_VERIFY_FAILED, and
    // the user sees a generic "Setting up" for an account that is stuck.
    AccountState.MAILBOX_VERIFYING,
    // A run's verdict lands on the state the account is in when the run
    // CONCLUDES, which is not always the state it started in. Anything that
    // moves a mailbox out of MAILBOX_VERIFYING mid-run leaves it here, and a
    // user disconnect + reconnect does exactly that — `claimRun` then refuses a
    // fresh run while the original is still in flight, so the original finishes
    // against a CONNECTED row. Without this edge that failure has nowhere to
    // land: the decision logs `effect.none.why.verify_failed_no_edge`, raises
    // the send/receive flag and leaves the badge reading like an ordinary
    // pending mailbox, and nothing re-tests it until the next daily Start.
    // Same hole WARMUP_READY documents below, reached from a different state —
    // 557220 lost a day to it on 2026-10-29, and 4 accounts hit it in 2.3 days.
    AccountState.MAILBOX_VERIFY_FAILED,
    AccountState.WARMUP_ACTIVE,
    AccountState.DISCONNECTED,
  ],
  [AccountState.DNS_CHECKING]: [
    AccountState.DNS_PASSED,
    AccountState.DNS_FAILED,
    AccountState.DISCONNECTED,
  ],
  [AccountState.DNS_FAILED]: [AccountState.DNS_CHECKING, AccountState.DISCONNECTED],
  // DNS_PASSED keeps its direct WARMUP_READY edge: mailbox verification is
  // enforced by the callers, not by removing the edge, and an account whose
  // verification already passed goes straight through.
  [AccountState.DNS_PASSED]: [
    AccountState.MAILBOX_VERIFYING,
    AccountState.WARMUP_READY,
    AccountState.DISCONNECTED,
  ],
  [AccountState.MAILBOX_VERIFYING]: [
    AccountState.WARMUP_READY,
    AccountState.WARMUP_ACTIVE,
    AccountState.MAILBOX_VERIFY_FAILED,
    AccountState.DISCONNECTED,
  ],
  // MAILBOX_VERIFY_FAILED → WARMUP_ACTIVE is deliberately ABSENT, mirroring
  // DNS_FAILED. A failed mailbox re-enters verification; it never shortcuts
  // into warmup.
  [AccountState.MAILBOX_VERIFY_FAILED]: [
    AccountState.MAILBOX_VERIFYING,
    AccountState.DISCONNECTED,
  ],
  // WARMUP_READY → MAILBOX_VERIFYING is what keeps a capacity-parked mailbox
  // from being stranded. `promoteFromVerificationPass` parks an account here
  // when the org is at its warming cap, and the daily health-check Start still
  // picks it up (`hasNeverWarmed`) and re-tests it every day. Without the edge
  // the run could neither start nor land a FAILED verdict anywhere — the effect
  // resolved to `none` (`verify_failed_no_edge`) and the mailbox burned six
  // seed emails a day forever with nothing user-visible.
  [AccountState.WARMUP_READY]: [
    AccountState.MAILBOX_VERIFYING,
    AccountState.WARMUP_ACTIVE,
    AccountState.DISCONNECTED,
  ],
  [AccountState.WARMUP_THROTTLED]: [
    AccountState.WARMUP_ACTIVE,
    AccountState.WARMUP_PAUSED,
    ...ANY_WARMUP_TO_SUSPENDED,
    AccountState.DISCONNECTED,
  ],
  [AccountState.WARMUP_ACTIVE]: [
    AccountState.WARMUP_THROTTLED,
    // graduation edge: the account moves straight to MAINTENANCE at its
    // maintenance cap (`StateMachineService.transitionToGraduated`).
    AccountState.MAINTENANCE,
    AccountState.WARMUP_PAUSED,
    ...ANY_WARMUP_TO_SUSPENDED,
    AccountState.DISCONNECTED,
  ],
  [AccountState.MAINTENANCE]: [
    AccountState.WARMUP_ACTIVE,
    // a graduated/maintenance account can be user-paused or disconnected (no status guard in legacy pauseAccount)
    AccountState.WARMUP_PAUSED,
    ...ANY_WARMUP_TO_SUSPENDED,
    AccountState.DISCONNECTED,
  ],
  // Resume restores prior warmup state: active, maintenance, or — for a mailbox
  // paused while throttled — back onto its throttle ladder where it left off.
  [AccountState.WARMUP_PAUSED]: [
    AccountState.WARMUP_ACTIVE,
    AccountState.WARMUP_THROTTLED,
    AccountState.MAINTENANCE,
    AccountState.DISCONNECTED,
  ],
  [AccountState.SUSPENDED_AUTH]: [...SUSPENDED_RECOVERY_TARGETS],
  [AccountState.SUSPENDED_BOUNCE]: [
    ...SUSPENDED_RECOVERY_TARGETS,
    // Support-only force-resume path only (never a user Resume — that stays
    // locked by `isUserResumable`/`isHardBounceSuspendReason`, unchanged):
    // re-verify the mailbox can actually send+receive before trusting it's
    // alive again, instead of resuming straight into WARMUP_ACTIVE on a
    // human's unverified "it's fixed" claim. See
    // `StateMachineService.beginForcedVerificationFromHardBounce` (the write)
    // and `HealthCheckService.forceVerifyHardBounce` (the caller, which starts
    // the actual test-email run). Eligible on any confirmed hard-bounce reason
    // (`isHardBounceSuspendReason`). Deliberately NOT added to the other three
    // states sharing `SUSPENDED_RECOVERY_TARGETS` — this edge is scoped to the
    // hard-bounce suspension this override exists for.
    AccountState.MAILBOX_VERIFYING,
  ],
  [AccountState.SUSPENDED_SPAM]: [...SUSPENDED_RECOVERY_TARGETS],
  [AccountState.SUSPENDED_BLACKLIST]: [...SUSPENDED_RECOVERY_TARGETS],
  [AccountState.SUSPENDED_PROVIDER]: [...SUSPENDED_RECOVERY_TARGETS],
  [AccountState.SUSPENDED_PLATFORM]: [...SUSPENDED_RECOVERY_TARGETS],
  [AccountState.SUSPENDED_VERIFICATION]: [...SUSPENDED_RECOVERY_TARGETS],
};

/** Whether `from → to` is a legal transition per `ALLOWED_TRANSITIONS`. */
export function isTransitionAllowed(from: AccountState, to: AccountState): boolean {
  return ALLOWED_TRANSITIONS[from].includes(to);
}

/**
 * The states a user may MANUALLY pause from (TRU-1044). All three actively send
 * mail, so all three must accept Pause — a throttled or post-graduation
 * (MAINTENANCE) mailbox is still warming, and Pause is the only affordance that
 * stops it.
 *
 * This is the ONE source of truth for "pausable", shared by:
 *   - `EmailAccountsService.pauseWarmup`      (single, apps/api)
 *   - `EmailAccountsRepository.findPausableIds` (bulk, apps/api)
 *   - `SENDING_WARMUP_STATUSES`               (apps/web, via `SENDING_STATUSES`)
 * The three previously disagreed: web offered Pause on THROTTLED/MAINTENANCE
 * while the API accepted only RUNNING, so the button 400'd (`022501`) with no
 * other way to stop warmup on those accounts.
 *
 * Deliberately NARROWER than `isTransitionAllowed(state, WARMUP_PAUSED)`, which
 * the ticket first proposed: `SUSPENDED_RECOVERY_TARGETS` includes
 * `WARMUP_PAUSED`, so that predicate is `true` for all six `SUSPENDED_*` states.
 * Routing a system-suspended account through the MANUAL pause path would
 * overwrite its `warmupPausedReason` (e.g. `SUSPENDED_BOUNCE` → `USER_PAUSED`)
 * and relabel a system stop as a user stop. Suspended accounts resume via
 * `resumeWarmup`; they are not manually pausable.
 *
 * The legacy-mirror equivalent is `SENDING_STATUSES` in `warmup.enum.ts` — the
 * two sets are kept in sync by `accountStateToLegacyWarmupStatus`.
 */
export const MANUALLY_PAUSABLE_STATES: ReadonlyArray<AccountState> = [
  AccountState.WARMUP_ACTIVE,
  AccountState.WARMUP_THROTTLED,
  AccountState.MAINTENANCE,
];

/**
 * Whether the user may manually pause this row's warmup.
 *
 * Reads BOTH columns on purpose. `accountState` is canonical and is what fixes
 * TRU-1044, but a state-only guard would REGRESS accounts whose `accountState`
 * is stranded behind a RUNNING mirror — a shipped bulk-enable bug wrote
 * `warmupStatus = RUNNING` while enqueuing `RESCHEDULE` (which performs no
 * transition), leaving `accountState` at its pre-enable value (see
 * apps/api/src/modules/email-accounts/CLAUDE.md, "Bulk ENABLE_WARMUP must
 * enqueue the 'ENABLED' event"). Those rows pause fine today via the mirror and
 * must keep pausing.
 *
 * So: pausable if EITHER column says the account is sending. This is a strict
 * superset of the old `warmupStatus === RUNNING` check — it can only ever accept
 * more accounts than before, never fewer. A genuinely stopped account fails both
 * halves (`WARMUP_PAUSED`/`SUSPENDED_*`/`PENDING` project to a non-sending
 * mirror AND a non-pausable state), so suspended accounts still route to
 * `resumeWarmup` rather than being relabelled as user-paused.
 */
export function isWarmupManuallyPausable(
  accountState: AccountState,
  warmupStatus: WarmupStatus,
): boolean {
  return (
    MANUALLY_PAUSABLE_STATES.includes(accountState) ||
    SENDING_STATUSES.includes(warmupStatus)
  );
}

/**
 * Suspension reasons a USER may not resume out of, matched as a case-insensitive
 * PREFIX against `warmupPausedReason`.
 *
 * What both share, and what nothing else on the suspend list shares: they are
 * computed from a ROLLING 7-DAY window (`DELIVERABILITY_WINDOW_MS` in
 * `PoolCalculatorService`) over the account's own sends (ClickHouse `warmupEmails`). The
 * evidence that suspended the account is therefore STILL IN THE WINDOW the
 * moment it resumes, so a user Resume cannot succeed — the next
 * `ThresholdEvaluatorService` tick recomputes the identical rate and re-suspends,
 * usually within the hour. Offering the button at all is a lie. These recover
 * only through `AutoResumeService`, which waits out the 24h cool-down and returns
 * the account onto the throttle ladder at half the volume it was suspended at,
 * so it earns its volume back on fresh evidence rather than on a calendar.
 *
 * NOT here, deliberately — every other suspend reason is EVENT-driven, with no
 * rolling number behind it, so resuming is a genuine retry rather than a
 * guaranteed re-trip:
 *   - `HARD_BOUNCE*` (BounceProcessor) — one dead-address verdict against the
 *     account as a RECIPIENT. Its own SENDING reputation was never in question,
 *     and the address stays on `HARD_BOUNCED_RECIPIENTS_KEY` either way, so a
 *     resume that turns out to be wrong costs nothing.
 *   - `BLACKLISTED:*` / `DNS_FAILURE:*` (both written by the warmup-worker
 *     health-check decision) — both user-fixable and both externally
 *     re-verified by the daily health check. A premature resume is
 *     re-suspended by the next Decide, which is self-correcting rather than a
 *     loop.
 *   - `AUTH_FAILED` — recovers on the reconnect event; a premature resume just
 *     re-suspends on the next 401.
 *
 * `SPAM_RATE` is absent on purpose even though it is equally rate-driven: BOTH
 * of its producers (`ThresholdEvaluatorService` and `SchedulerService`'s ramp
 * PAUSE branch) land on `SUSPENDED_SPAM`, so the STATE alone settles that case
 * and no reason parsing is needed. Only `SUSPENDED_BOUNCE` is mixed.
 */
export const RATE_DRIVEN_SUSPEND_REASON_PREFIXES: ReadonlyArray<string> = [
  // ThresholdEvaluatorService, via `buildThrottleReason(THROTTLE_DIMENSION.BOUNCE_RATE, ...)`
  // e.g. `BOUNCE_RATE_6.25_PCT_NEW_DOMAIN`.
  'BOUNCE_RATE',
  // DeliveryGateService — unconfirmed-delivery rate, a bounce-family signal.
  'HIGH_UNCONFIRMED_RATE',
];

/**
 * Prefix-match a stored `warmupPausedReason` against
 * {@link RATE_DRIVEN_SUSPEND_REASON_PREFIXES}. Case- and whitespace-insensitive
 * because that column is a free-text `varchar` written by seven different
 * producers, not a constrained vocabulary.
 */
function isRateDrivenSuspendReason(warmupPausedReason: string | null): boolean {
  if (!warmupPausedReason) return false;
  const normalized = warmupPausedReason.trim().toUpperCase();
  return RATE_DRIVEN_SUSPEND_REASON_PREFIXES.some((prefix) => normalized.startsWith(prefix));
}

/**
 * The `warmupPausedReason` prefix `BounceProcessor.disconnectRecipient` writes
 * for a confirmed HARD bounce (`HARD_BOUNCE` or `HARD_BOUNCE:<matched reason>`).
 * Declared once so producer (`bounce.processor.ts`) and the repeat-offense
 * check below reference the same literal.
 */
export const HARD_BOUNCE_SUSPEND_REASON_PREFIX = 'HARD_BOUNCE';

/** Whether a stored `warmupPausedReason` describes a confirmed HARD bounce. */
export function isHardBounceSuspendReason(warmupPausedReason: string | null): boolean {
  if (!warmupPausedReason) return false;
  return warmupPausedReason.trim().toUpperCase().startsWith(HARD_BOUNCE_SUSPEND_REASON_PREFIX);
}

/**
 * The `warmupPausedReason` prefix the warmup-worker health-check decision
 * writes when a required DNS record has regressed — the full reason is `DNS_FAILURE: SPF, MX`, i.e. the
 * prefix plus the blocking record list.
 *
 * Declared once because this string is the ONLY thing separating the two very
 * different populations that share `SUSPENDED_BLACKLIST`: a real DNSBL listing
 * (`BLACKLISTED:*`) and a DNS regression (`DNS_FAILURE:*`), both written by the
 * health-check decision. Their recovery signals are different — a clear
 * `blacklistedAt` for the first, a passing DNS gate for the second — so the
 * health-check decision's resume rule has to tell them apart, and it must not
 * do so on a fifth hand-typed copy of the literal. `warmupPausedReason` is an
 * unconstrained free-text `varchar`, so a typo here is invisible: the branch
 * simply never runs and the account silently falls through to the wrong recovery
 * check. Producer and consumer both reference this constant.
 */
export const DNS_FAILURE_SUSPEND_REASON_PREFIX = 'DNS_FAILURE';

/**
 * Whether a stored `warmupPausedReason` describes a DNS regression.
 *
 * Case- and whitespace-insensitive for the same reason as
 * {@link isRateDrivenSuspendReason} — seven different producers write that
 * column and none of them shares a vocabulary.
 */
export function isDnsFailureSuspendReason(warmupPausedReason: string | null): boolean {
  if (!warmupPausedReason) return false;
  return warmupPausedReason.trim().toUpperCase().startsWith(DNS_FAILURE_SUSPEND_REASON_PREFIX);
}

/**
 * The `warmupPausedReason` prefix written when an authoritative DNSBL listing
 * suspends a warming account — the full reason is `BLACKLISTED: <zone,…>`.
 *
 * The other half of `SUSPENDED_BLACKLIST` (see
 * {@link DNS_FAILURE_SUSPEND_REASON_PREFIX}). Written by warmup-worker's
 * health-check Decide, read by that same Decide (a listing that has cleared
 * resumes the account) and by the health-check Start target query, which picks
 * `SUSPENDED_BLACKLIST` only for these two reasons. Declared once so the
 * producer and every consumer agree on the spelling of a free-text column.
 */
export const BLACKLISTED_SUSPEND_REASON_PREFIX = 'BLACKLISTED';

/** Whether a stored `warmupPausedReason` describes an authoritative DNSBL listing. */
export function isBlacklistedSuspendReason(warmupPausedReason: string | null): boolean {
  if (!warmupPausedReason) return false;
  return warmupPausedReason.trim().toUpperCase().startsWith(BLACKLISTED_SUSPEND_REASON_PREFIX);
}

/**
 * The `warmupPausedReason` prefix the mailbox-verification orchestrator writes
 * when a warming account fails re-verification — the full reason is
 * `MAILBOX_VERIFICATION_FAILED: NOT_FOUND_INBOUND`, i.e. the prefix plus the
 * run's `failureReason`.
 *
 * Declared once for the same reason as {@link DNS_FAILURE_SUSPEND_REASON_PREFIX}:
 * `warmupPausedReason` is unconstrained free text, so a typo is invisible —
 * the branch never runs and the account falls through to the wrong recovery
 * check. Producer and consumer both reference this constant.
 *
 * ⚠ The suffix frequently CONTAINS the word "bounce" (`HARD_BOUNCE_INBOUND`),
 * which is exactly why `reasonToSuspendedState` tests for verification FIRST.
 */
export const MAILBOX_VERIFICATION_SUSPEND_REASON_PREFIX = 'MAILBOX_VERIFICATION_FAILED';

/** Whether a stored `warmupPausedReason` describes a mailbox-verification failure. */
export function isMailboxVerificationSuspendReason(
  warmupPausedReason: string | null,
): boolean {
  if (!warmupPausedReason) return false;
  return warmupPausedReason
    .trim()
    .toUpperCase()
    .startsWith(MAILBOX_VERIFICATION_SUSPEND_REASON_PREFIX);
}

/**
 * The `warmupPausedReason` `StateMachineService.handleAuthFailed` writes for
 * every `AUTH_FAILED` producer (oauth refresh, partner minter, push-health,
 * `escalateEspFailure`, `handleSocketDead`, `SmtpSessionOwnershipService.
 * openOwnedSocket`) — always this exact literal, never interpolated.
 *
 * Declared once for the same reason as {@link DNS_FAILURE_SUSPEND_REASON_PREFIX}:
 * a hand-typed second copy of the literal can drift silently. Producer and
 * {@link isUserResumable} both reference this constant.
 */
export const AUTH_FAILED_SUSPEND_REASON = 'AUTH_FAILED';

/**
 * Whether a stored `warmupPausedReason` describes a dead-credential suspension —
 * as opposed to `SUSPENDED_AUTH`'s other producer, `SEND_FAILURES_EXCEEDED`
 * (3 failed sends in 24h, often transient). The two need different treatment in
 * {@link isUserResumable}: resuming a dead credential without fixing it just
 * re-suspends on the next send attempt, so that reason locks Resume; a
 * send-failure spike may well have cleared on its own, so that one does not.
 */
export function isAuthFailedSuspendReason(warmupPausedReason: string | null): boolean {
  if (!warmupPausedReason) return false;
  return warmupPausedReason.trim().toUpperCase().startsWith(AUTH_FAILED_SUSPEND_REASON);
}

/**
 * May the USER resume this account themselves?
 *
 * The ONE source of truth for "resumable", shared by all three layers so they
 * cannot drift the way pause did in TRU-1044 (web offered a button the API
 * rejected with `022501`, leaving those accounts with no working warmup control
 * at all):
 *   - `apps/web` `canResumeWarmup`              — whether the affordance renders
 *   - `apps/api` `resumeWarmup`                 — the user-facing 4xx
 *   - `apps/warmup-worker` `resumeFromPause`    — the check that actually HOLDS
 *
 * The worker is the enforcement point, not the api. The api writes no
 * `accountState`/`warmupStatus` at all (single-writer rule — only
 * `TransitionService` does); it enqueues a `RESUMED` event and returns. A gate
 * that does not own the write is advisory, so the worker re-checks against the
 * row it loads under the transition's lock.
 *
 * Deliberately NARROWER than `isTransitionAllowed(state, WARMUP_ACTIVE)` and than
 * the `SUSPENDED_STATES.includes(state)` test `resumeWarmup` used to run. The
 * transition table must keep every `SUSPENDED_* -> WARMUP_ACTIVE` edge open
 * because `AutoResumeService` travels them; this answers the different question
 * of whether a HUMAN may travel them.
 *
 * Applies to the MANUAL path only. `autoResume` / `autoResumeFromSuspend` are
 * untouched and remain the recovery route for everything locked here — nothing
 * this function refuses is stranded, it just recovers on the system's schedule
 * instead of on a click.
 *
 * An unknown or null reason under `SUSPENDED_BOUNCE` reads as RESUMABLE —
 * fail-OPEN, chosen on which error costs more. Wrongly locking a hard bounce
 * strands the mailbox permanently, because `findDueSuspendedStates` excludes
 * `permanentlyDisabled` rows and a user Resume is the only writer that clears
 * that flag — there would be no way back at all. Wrongly unlocking a rate
 * suspension costs one wasted click before the next evaluator tick re-suspends
 * it. Fail-open is also what lets this ship with NO data migration: rows
 * suspended before `HARD_BOUNCE:`-prefixing landed carry the bare matched phrase
 * (`user unknown`, `status:5.1.1`) and are still read correctly as resumable.
 * `SUSPENDED_AUTH` is the second mixed state and fails OPEN the same way for
 * the same reason (only two producers write it, both exact internal literals —
 * see `isAuthFailedSuspendReason` — so "unrecognized" cannot actually happen
 * short of a bug, and locking on it would strand-by-default on that bug).
 */
export function isUserResumable(
  accountState: AccountState,
  warmupPausedReason: string | null,
): boolean {
  switch (accountState) {
    // User-initiated stop — always the user's to undo.
    case AccountState.WARMUP_PAUSED:
    // Infrastructure, not deliverability: the mailbox's reputation is intact.
    // Self-heals on a CONNECTED event (`resumeFromAuthProviderSuspendOnReconnect`)
    // regardless of the reason below — reconnecting always restores it. Resume
    // is a manual retry, and unlike SUSPENDED_AUTH it has only ONE producer
    // (`PARTNER_UNAVAILABLE`), so there is no reason to split.
    /* falls through */
    case AccountState.SUSPENDED_PROVIDER:
    // Both reasons are user-fixable and externally re-verified by the daily
    // health check — `BLACKLISTED:` and `DNS_FAILURE:`, both written by its
    // decision. Resuming before the underlying fix lands is re-suspended by the
    // next Decide rather than looping on stale numbers.
    // falls through
    case AccountState.SUSPENDED_BLACKLIST:
      return true;
    // The other mixed state: `AUTH_FAILED` is a dead credential — OAuth grant
    // revoked, SMTP password changed, or (for a partner-managed mailbox) the
    // partner's own auth broken — and clicking Resume without fixing that just
    // re-suspends on the very next send. `SEND_FAILURES_EXCEEDED` lands in the
    // same state but is often transient, so it stays resumable. Locking the
    // account does not strand it: the CONNECTED reconnect path above is
    // unconditional on the reason and keeps working either way.
    case AccountState.SUSPENDED_AUTH:
      return !isAuthFailedSuspendReason(warmupPausedReason);
    // The other mixed state below it: a HARD bounce is a single dead-address
    // event and resumable, while the two rate-driven reasons that also land
    // here are not.
    case AccountState.SUSPENDED_BOUNCE:
      return !isRateDrivenSuspendReason(warmupPausedReason);
    // SUSPENDED_VERIFICATION is deliberately NOT resumable. It recovers by
    // re-verifying, not by a button: a Resume would put a mailbox that
    // provably cannot send or receive straight back into warmup, and the next
    // sweep would re-suspend it. The auto-resume sweep releases it the moment a
    // run PASSES.
    case AccountState.SUSPENDED_VERIFICATION:
      return false;
    // SUSPENDED_SPAM (both producers rate-driven — see the prefix list's note),
    // SUSPENDED_PLATFORM (a platform/abuse stop is never the user's to lift),
    // and every non-stopped state, which has nothing to resume and is reported
    // separately by `resumeWarmup` as `ResumeWarmup.NotSuspended`.
    default:
      return false;
  }
}

/**
 * Suspensions that are NEVER released by the warmup-worker's auto-resume paths,
 * so they sit until the user acts (the Action items page lists these).
 *
 * The complement of everything that releases itself:
 *   - `SUSPENDED_BOUNCE` / `SPAM` — the 30-min auto-resume sweep, on a cool-down.
 *   - `SUSPENDED_BLACKLIST` — warmup-worker's daily health-check Decide, once
 *     the DNS gate passes again (`DNS_FAILURE:*`) or the listing clears
 *     (`BLACKLISTED:*`). The three together are `DELIVERABILITY_SUSPENDED_STATES`
 *     in warmup-worker's state-machine constants.
 *   - `SUSPENDED_VERIFICATION` — released by a later PASSING verification run.
 * What is left: `AUTH` / `PROVIDER` are ESCALATION-ONLY (the sweep only alerts;
 * recovery is a reconnect or a user Resume) and `PLATFORM` has no auto path.
 *
 * ⚠ Must stay in lockstep with warmup-worker's routing. A drift-guard spec in
 * `apps/warmup-worker` asserts `USER_ACTION_SUSPENDED_STATES` ∪
 * `DELIVERABILITY_SUSPENDED_STATES` ∪ `{SUSPENDED_VERIFICATION}` is exactly
 * `SUSPENDED_STATES`; add an auto-resume route for one of these and it fails.
 */
export const USER_ACTION_SUSPENDED_STATES: ReadonlyArray<AccountState> = [
  AccountState.SUSPENDED_AUTH,
  AccountState.SUSPENDED_PROVIDER,
  AccountState.SUSPENDED_PLATFORM,
];

/**
 * Is this suspension one the system will never lift on its own?
 *
 * `permanentlyDisabled` is the second half: `findDueSuspendedStates` excludes
 * those rows from the sweep whatever their state, so a hard-bounced (dead
 * address) mailbox in `SUSPENDED_BOUNCE` is stranded until the user resumes it.
 * Only true for a `SUSPENDED_*` state — a stale `permanentlyDisabled` on a
 * healthy account is not an action item.
 */
export function requiresUserActionToResume(
  accountState: AccountState,
  permanentlyDisabled: boolean,
): boolean {
  if (USER_ACTION_SUSPENDED_STATES.includes(accountState)) return true;
  return permanentlyDisabled && SUSPENDED_STATES.includes(accountState);
}

/**
 * Is this a suspension the warmup-worker's 30-min auto-resume sweep releases on
 * its own once the cool-down ends (onto the throttle ladder)? Drives the public
 * `cooldown` bucket.
 *
 * Mirrors the sweep's own selection, not a looser reading of it:
 *   - `SUSPENDED_SPAM` — both producers are rate-driven, so the state settles it.
 *   - `SUSPENDED_BOUNCE` only for a RATE-driven reason
 *     ({@link RATE_DRIVEN_SUSPEND_REASON_PREFIXES}). A `HARD_BOUNCE:` reason is
 *     escalated to `permanentlyDisabled` by the sweep instead of released, and an
 *     unknown/legacy reason is not positively known to be released, so both stay
 *     `suspended` — claiming "resumes in Xh" for a mailbox that won't is worse
 *     than under-claiming.
 *   - `permanentlyDisabled` rows are excluded from the sweep whatever their
 *     state, so they are never cooling down.
 *
 * ⚠ The api's `ACCOUNT_STATE_BUCKET_SQL` restates this predicate in SQL for the
 * `?status=` filter and the counts; change both together.
 */
export function isCooldownSuspension(
  accountState: AccountState,
  warmupPausedReason: string | null,
  permanentlyDisabled = false,
): boolean {
  if (permanentlyDisabled) return false;
  if (accountState === AccountState.SUSPENDED_SPAM) return true;
  return (
    accountState === AccountState.SUSPENDED_BOUNCE &&
    isRateDrivenSuspendReason(warmupPausedReason)
  );
}

/**
 * The public status bucket for an account: {@link ACCOUNT_STATE_BUCKET},
 * refined to `cooldown` for a suspension that resumes on its own. The one
 * derivation every server projection of `statusBucket` uses.
 */
export function statusBucketFor(
  accountState: AccountState,
  warmupPausedReason: string | null,
  permanentlyDisabled = false,
): StatusBucket {
  return isCooldownSuspension(accountState, warmupPausedReason, permanentlyDisabled)
    ? 'cooldown'
    : ACCOUNT_STATE_BUCKET[accountState];
}

/**
 * The `TransitionTrigger` that pairs with a given `SUSPENDED_*` state. Shared by
 * every suspend call site so the audit trigger always matches the target state.
 * Throws on a non-suspended state — callers only pass suspend targets here.
 */
export function suspendTriggerForState(suspendState: AccountState): TransitionTrigger {
  switch (suspendState) {
    case AccountState.SUSPENDED_SPAM:
      return TransitionTrigger.SUSPEND_SPAM;
    case AccountState.SUSPENDED_BOUNCE:
      return TransitionTrigger.SUSPEND_BOUNCE;
    case AccountState.SUSPENDED_BLACKLIST:
      return TransitionTrigger.SUSPEND_BLACKLIST;
    case AccountState.SUSPENDED_AUTH:
      return TransitionTrigger.SUSPEND_AUTH;
    case AccountState.SUSPENDED_PROVIDER:
      return TransitionTrigger.SUSPEND_PROVIDER;
    case AccountState.SUSPENDED_PLATFORM:
      return TransitionTrigger.SUSPEND_PLATFORM;
    case AccountState.SUSPENDED_VERIFICATION:
      return TransitionTrigger.SUSPEND_VERIFICATION;
    default:
      throw new Error(`suspendTriggerForState called with non-suspended state: ${suspendState}`);
  }
}

/**
 * Map the legacy split representation onto a canonical `AccountState`, used by
 * the DB backfill and the transition shim (plan §5 step 2).
 *
 * @param status        legacy `EmailAccount.status`
 * @param warmupStatus  legacy `EmailAccountWarmupState.warmupStatus`
 * @param opts.dnsAllValid  all four DNS records (SPF/DKIM/DMARC/MX) valid
 * @param opts.dnsChecked   DNS has been checked at least once (`domainSecurityInfo.recordsRefreshedAt` set)
 * @param opts.pausedReason free-text `warmupPausedReason`, inspected only for PAUSED
 */
export function legacyToAccountState(
  status: AccountStatus,
  warmupStatus: WarmupStatus,
  opts?: { dnsAllValid?: boolean; dnsChecked?: boolean; pausedReason?: string | null },
): AccountState {
  if (status === AccountStatus.DISCONNECTED) {
    return AccountState.DISCONNECTED;
  }

  switch (warmupStatus) {
    case WarmupStatus.PENDING:
      if (opts?.dnsAllValid) return AccountState.WARMUP_READY;
      if (opts?.dnsChecked && !opts?.dnsAllValid) return AccountState.DNS_FAILED;
      return AccountState.CONNECTED;
    case WarmupStatus.RUNNING:
      return AccountState.WARMUP_ACTIVE;
    case WarmupStatus.THROTTLED:
      return AccountState.WARMUP_THROTTLED;
    case WarmupStatus.MAINTENANCE:
      return AccountState.MAINTENANCE;
    case WarmupStatus.PAUSED:
      return reasonToSuspendedState(opts?.pausedReason);
    default:
      return AccountState.WARMUP_PAUSED;
  }
}

/**
 * Classify a free-text `warmupPausedReason` into a suspended state, or
 * `WARMUP_PAUSED` for genuine user/system pauses. Its only live consumer is the
 * backfill (`legacyToAccountState`) — runtime suspend callers pass an explicit
 * `SUSPENDED_*` state, not a reason string. Substring match is case-insensitive
 * and tolerant of the real reason formats (e.g. `SPAM_RATE_30_PCT_...`,
 * `Spam rate 30%`, `DNS_FAILURE: ...`).
 *
 * Order matters: blacklist/dns are checked BEFORE spam because real blacklist
 * provider names (e.g. `BLACKLIST_SPAMHAUS`) contain the substring "spam";
 * spam/bounce/auth fallbacks follow so a compound reason routes to the most
 * specific suspended state.
 */
export function reasonToSuspendedState(pausedReason?: string | null): AccountState {
  if (!pausedReason) return AccountState.WARMUP_PAUSED;
  const reason = pausedReason.toLowerCase();

  // Mailbox verification is tested FIRST — before every other family — because
  // its reason string embeds the run's failureReason, which routinely contains
  // "bounce" (`MAILBOX_VERIFICATION_FAILED: HARD_BOUNCE_INBOUND`). Tested any
  // later, a verification failure would be misfiled as SUSPENDED_BOUNCE and
  // handed to the bounce recovery signal, which would never release it.
  // Nothing in the other families contains "verification", so testing it first
  // steals no reason from them.
  if (reason.includes('verification')) {
    return AccountState.SUSPENDED_VERIFICATION;
  }
  // Blacklist + DNS regression both group under blacklist per the matrix.
  // Checked before spam because real blacklist provider names (e.g.
  // "BLACKLIST_SPAMHAUS") contain the substring "spam".
  if (reason.includes('blacklist') || reason.includes('dns')) {
    return AccountState.SUSPENDED_BLACKLIST;
  }
  // Spam family (includes inbox-placement regressions).
  if (reason.includes('spam') || reason.includes('inbox_placement')) {
    return AccountState.SUSPENDED_SPAM;
  }
  // Bounce family (includes high-unconfirmed-rate).
  if (reason.includes('bounce') || reason.includes('unconfirmed')) {
    return AccountState.SUSPENDED_BOUNCE;
  }
  if (reason.includes('auth') || reason.includes('disconnect')) {
    return AccountState.SUSPENDED_AUTH;
  }
  // USER_PAUSED, WORKSPACE_DELETED, "Plan downgraded ...", and anything else
  // are genuine pauses, not suspensions.
  return AccountState.WARMUP_PAUSED;
}

/**
 * Project a canonical `AccountState` onto the legacy `WarmupStatus` so the
 * state guard can dual-write the legacy mirror column during migration (R4).
 * This is a lossy, one-directional projection — multiple `AccountState`s collapse
 * onto one `WarmupStatus`:
 *
 *   - all six `SUSPENDED_*` -> `PAUSED` (legacy faked every suspend as PAUSED;
 *     the distinction lives only in `accountState` now — preserved in the mirror only)
 *   - connection states (`DISCONNECTED`/`CONNECTED`/`DNS_*`/`WARMUP_READY`) ->
 *     `PENDING` (legacy `warmupStatus` had no connection vocabulary; PENDING is
 *     "warmup not yet running", the closest legacy meaning)
 *
 * Used ONLY by the transition shim's dual-write; consumers reading state should
 * move to `accountState`.
 */
export function accountStateToLegacyWarmupStatus(state: AccountState): WarmupStatus {
  switch (state) {
    case AccountState.WARMUP_ACTIVE:
      return WarmupStatus.RUNNING;
    case AccountState.WARMUP_THROTTLED:
      return WarmupStatus.THROTTLED;
    case AccountState.MAINTENANCE:
      return WarmupStatus.MAINTENANCE;
    case AccountState.WARMUP_PAUSED:
    case AccountState.SUSPENDED_AUTH:
    case AccountState.SUSPENDED_BOUNCE:
    case AccountState.SUSPENDED_SPAM:
    case AccountState.SUSPENDED_BLACKLIST:
    case AccountState.SUSPENDED_PROVIDER:
    case AccountState.SUSPENDED_PLATFORM:
    case AccountState.SUSPENDED_VERIFICATION:
      return WarmupStatus.PAUSED;
    // ⚠ Both mailbox-verification states collapse onto PENDING, which means a
    // mid-verification account is INDISTINGUISHABLE from an unpromoted one in
    // the legacy mirror. Anything gating on "is this account ready to promote"
    // must read `accountState`, never `warmupStatus === PENDING`, or it will
    // promote accounts whose verification is still in flight.
    // (Kept above the group: a comment between empty case labels trips `no-fallthrough`.)
    case AccountState.DISCONNECTED:
    case AccountState.CONNECTED:
    case AccountState.DNS_CHECKING:
    case AccountState.DNS_FAILED:
    case AccountState.DNS_PASSED:
    case AccountState.MAILBOX_VERIFYING:
    case AccountState.MAILBOX_VERIFY_FAILED:
    case AccountState.WARMUP_READY:
      return WarmupStatus.PENDING;
  }
}

/**
 * Plain-language cause of a transition, used as a structured status's `why` when the
 * caller gave no reason. The raw trigger code (`USER_RESUME`) must never reach the UI.
 * `satisfies` fails the build when a trigger is added without copy.
 */
export const TRANSITION_TRIGGER_DESCRIPTION = {
  [TransitionTrigger.USER_ENABLE]: 'Warmup was turned on.',
  [TransitionTrigger.USER_PAUSE]: 'Warmup was paused manually.',
  [TransitionTrigger.USER_RESUME]: 'Warmup was resumed manually.',
  [TransitionTrigger.USER_DISCONNECT]: 'The mailbox was disconnected.',
  [TransitionTrigger.RAMP_PAUSE]: 'The daily ramp-up was paused.',
  [TransitionTrigger.THROTTLE]: 'A deliverability signal dipped, so sending volume was lowered.',
  [TransitionTrigger.THROTTLE_RECOVER]: 'Deliverability recovered, so full sending volume was restored.',
  [TransitionTrigger.GRADUATE]: 'The account met the graduation criteria.',
  [TransitionTrigger.MAINTENANCE_DECAY]: 'Inbox placement declined while in maintenance.',
  [TransitionTrigger.SUSPEND_SPAM]: 'Too many warmup emails landed in spam.',
  [TransitionTrigger.SUSPEND_BOUNCE]: 'Too many warmup emails bounced.',
  [TransitionTrigger.SUSPEND_BLACKLIST]: 'The sending domain or IP was found on a blacklist.',
  [TransitionTrigger.SUSPEND_AUTH]: "We couldn't sign in to this mailbox.",
  [TransitionTrigger.SUSPEND_PROVIDER]: 'The email provider blocked or limited sending.',
  [TransitionTrigger.SUSPEND_PLATFORM]: 'An internal platform issue paused warmup.',
  [TransitionTrigger.AUTO_RESUME]: 'Warmup resumed automatically once the issue cleared.',
  [TransitionTrigger.DNS_CHECK_START]: "We're checking the domain's DNS records.",
  [TransitionTrigger.DNS_CHECK_PASS]: "The domain's DNS records passed the check.",
  [TransitionTrigger.DNS_CHECK_FAIL]: "The domain's DNS records failed the check.",
  [TransitionTrigger.CONNECT]: 'The mailbox was connected.',
  [TransitionTrigger.WORKSPACE_DELETED]: 'The workspace this mailbox belonged to was deleted.',
  [TransitionTrigger.VERIFY_START]: "We're checking that this mailbox can send and receive.",
  [TransitionTrigger.VERIFY_PASS]: 'The mailbox passed its send and receive check.',
  [TransitionTrigger.VERIFY_FAIL]: 'The mailbox failed its send and receive check.',
  [TransitionTrigger.SUSPEND_VERIFICATION]: 'The mailbox stopped sending or receiving.',
  [TransitionTrigger.POOL_PROMOTE]: 'The mailbox moved up to a higher pool.',
  [TransitionTrigger.POOL_DEMOTE]: 'The mailbox moved down to a lower pool.',
} as const satisfies Record<TransitionTrigger, string>;

/**
 * Triggers that record a pool-tier move rather than a lifecycle change. Their
 * rows are audit-only: every reader that treats the newest row as the account's
 * current status must skip them, or a promotion would hide a throttle or
 * suspension explanation.
 */
export const POOL_TIER_TRANSITION_TRIGGERS: readonly TransitionTrigger[] = [
  TransitionTrigger.POOL_PROMOTE,
  TransitionTrigger.POOL_DEMOTE,
];

/** Plain-language name of each state, used as a structured status's `what` when no case-specific copy exists. */
export const ACCOUNT_STATE_DESCRIPTION = {
  [AccountState.DISCONNECTED]: 'Mailbox disconnected',
  [AccountState.CONNECTED]: 'Mailbox connected',
  [AccountState.DNS_CHECKING]: 'Checking DNS records',
  [AccountState.DNS_FAILED]: 'DNS check failed',
  [AccountState.DNS_PASSED]: 'DNS check passed',
  [AccountState.MAILBOX_VERIFYING]: 'Checking this mailbox',
  [AccountState.MAILBOX_VERIFY_FAILED]: 'Mailbox check failed',
  [AccountState.WARMUP_READY]: 'Ready to warm up',
  [AccountState.WARMUP_ACTIVE]: 'Warmup active',
  [AccountState.WARMUP_THROTTLED]: 'Warmup volume reduced',
  [AccountState.MAINTENANCE]: 'Warmup in maintenance',
  [AccountState.WARMUP_PAUSED]: 'Warmup paused',
  [AccountState.SUSPENDED_AUTH]: 'Warmup suspended - sign-in failed',
  [AccountState.SUSPENDED_BOUNCE]: 'Warmup suspended - bounces',
  [AccountState.SUSPENDED_SPAM]: 'Warmup suspended - spam',
  [AccountState.SUSPENDED_BLACKLIST]: 'Warmup suspended - blacklisted',
  [AccountState.SUSPENDED_PROVIDER]: 'Warmup suspended - provider issue',
  [AccountState.SUSPENDED_PLATFORM]: 'Warmup suspended - platform issue',
  [AccountState.SUSPENDED_VERIFICATION]: 'Warmup suspended - mailbox check failed',
} as const satisfies Record<AccountState, string>;

// Legacy fallback copy the structured-status builder wrote before the maps above existed.
const LEGACY_TRIGGER_WHY = /^Trigger: ([A-Z_]+)\.?$/;

/**
 * "Spam complaints" was renamed "spam rate" (2026-10-29). Transition rows and
 * notifications written before keep the old prose, so readers rewrite it on the
 * way out ("Spam complaints reached 4.00%…" → "Spam rate reached 4.00%…",
 * "…spam complaints too high" → "…spam rate too high"). Idempotent.
 */
export function renameSpamComplaints(text: string): string {
  return text.replace(/\b([Ss])pam complaints\b/g, (_m, s: string) => `${s}pam rate`);
}
const LEGACY_STATE_WHAT = /^State changed to ([A-Z_]+)$/;

/**
 * Rewrite legacy raw-code copy ("Trigger: USER_RESUME.", "State changed to DNS_PASSED")
 * still persisted on older transition rows into the plain-language text above. An
 * unknown code is left as-is, and the old "spam complaints" wording reads as "spam
 * rate" (`renameSpamComplaints`). Returns the same object when nothing matched.
 */
export function humanizeStructuredStatus(status: StructuredStatus): StructuredStatus {
  const why = LEGACY_TRIGGER_WHY.exec(status.why ?? '')?.[1];
  const what = LEGACY_STATE_WHAT.exec(status.what ?? '')?.[1];
  const whyText = why ? (TRANSITION_TRIGGER_DESCRIPTION as Record<string, string>)[why] : undefined;
  const whatText = what ? (ACCOUNT_STATE_DESCRIPTION as Record<string, string>)[what] : undefined;
  const rename = (t: string | null | undefined) => (t == null ? t : renameSpamComplaints(t));
  const nextWhy = rename(whyText ?? status.why);
  const nextWhat = rename(whatText ?? status.what);
  if (nextWhy === status.why && nextWhat === status.what) return status;
  return { ...status, why: nextWhy as StructuredStatus['why'], what: nextWhat as StructuredStatus['what'] };
}
